import { useState, useEffect, useRef } from "react";
import { useAppStore } from "@/store";
import {
  bytesToFloat32,
  createWaveformProcessor,
  WaveformProcessor,
} from "@/modules/audio-recording/waveform";
import { Uuid } from "@/domains/global/identifiers";

export interface TimeDomainDataSource {
  fftSize: number;
  getFloatTimeDomainData?(dataArray: Float32Array): void;
  getByteTimeDomainData?(dataArray: Uint8Array): void;
}

export const useAudioCaptureWaveform = ({
  associatedNoteId,
}: {
  associatedNoteId?: Uuid | null;
}) => {
  const { store } = useAppStore();
  const [waveFormBarHeights, setWaveFormBarHeights] = useState<number[]>();
  const processorRef = useRef<WaveformProcessor>(createWaveformProcessor());
  const isPausedRef = useRef(store.audio.recording.isPaused);

  useEffect(() => {
    isPausedRef.current = store.audio.recording.isPaused;
  }, [store.audio.recording.isPaused]);

  useEffect(() => {
    const visualizer = store.audio.recording.visualizer as TimeDomainDataSource | undefined;

    if (store.audio.activeRecordingNoteId !== associatedNoteId) {
      processorRef.current.reset();
      setWaveFormBarHeights(undefined);
      return;
    }

    if (!visualizer) {
      processorRef.current.reset();
      setWaveFormBarHeights(undefined);
      return;
    }

    const floatDomainData = new Float32Array(visualizer.fftSize);
    const byteDomainData = new Uint8Array(visualizer.fftSize);
    let frameCounter = 0;
    let animationFrameId: number;

    const animate = () => {
      animationFrameId = requestAnimationFrame(animate);

      // Only update every 12th frame
      frameCounter++;
      if (frameCounter % 12 !== 0) return;

      const isRecording = store.audio.activeRecording?.isRecording;

      if (!isRecording) {
        processorRef.current.reset();
        setWaveFormBarHeights(undefined);
        return;
      }

      if (isPausedRef.current) {
        return;
      }

      if (visualizer.getFloatTimeDomainData) {
        visualizer.getFloatTimeDomainData(floatDomainData);
      } else if (visualizer.getByteTimeDomainData) {
        visualizer.getByteTimeDomainData(byteDomainData);
        const converted = bytesToFloat32(byteDomainData);
        floatDomainData.set(converted);
      } else {
        return;
      }

      const heights = processorRef.current.process(floatDomainData);
      setWaveFormBarHeights(heights);
    };

    animate();
    return () => {
      cancelAnimationFrame(animationFrameId);
      // eslint-disable-next-line react-hooks/exhaustive-deps
      processorRef.current.reset();
      setWaveFormBarHeights(undefined);
    };
  }, [
    associatedNoteId,
    store.audio.activeRecording?.isRecording,
    store.audio.activeRecordingNoteId,
    store.audio.recording.visualizer,
  ]);

  return { waveFormBarHeights };
};
